"use client";

import React, { createContext, useContext, useState } from "react";
import { useDeepCompareEffect } from "@/hooks/use-deep-compare-effect";

export interface SortableItem {
  id: string;
  sortable?: boolean; // ソート可能かどうかを示すフラグ
  [key: string]: unknown;
}

type SortableContextType = {
  items: SortableItem[];
  setItems: React.Dispatch<React.SetStateAction<SortableItem[]>>;
  saveOrder: (items: SortableItem[]) => Promise<void>;
};

const SortableContext = createContext<SortableContextType | null>(null);

interface SortableProviderProps<T extends SortableItem> {
  children: React.ReactNode;
  initialItems: T[];
  onSaveOrder?: (items: T[]) => Promise<void>;
}

export function SortableProvider<T extends SortableItem>({
  children,
  initialItems,
  onSaveOrder,
}: SortableProviderProps<T>) {
  const [items, setItems] = useState<T[]>(initialItems);

  // initialItemsが変更されたら、itemsステートを更新
  useDeepCompareEffect(() => {
    setItems(initialItems);
  }, [initialItems]);

  const saveOrder = async (itemsToSave: SortableItem[]) => {
    if (onSaveOrder) {
      await onSaveOrder(itemsToSave as T[]);
    }
  };

  return (
    <SortableContext.Provider
      value={{
        items: items as SortableItem[],
        setItems: setItems as unknown as React.Dispatch<
          React.SetStateAction<SortableItem[]>
        >,
        saveOrder,
      }}
    >
      {children}
    </SortableContext.Provider>
  );
}

export function useSortable<T extends SortableItem>() {
  const context = useContext(SortableContext);
  if (!context) {
    throw new Error("useSortable must be used within a SortableProvider");
  }

  return {
    ...context,
    items: context.items as unknown as T[],
    setItems: context.setItems as unknown as React.Dispatch<
      React.SetStateAction<T[]>
    >,
  };
}
